import {
    AccountSettings,
    ListWebhookDeliveriesParams,
    OfficeOrOrgSettings,
    OrganizationSettings,
    ReservationTimeSettings,
    ReservationTimeSettingsResponse,
    WebhookDelivery,
    WebhookDeliverySummary,
    WebhookEndpoint,
    WebhookEndpointCreateRequest,
    WebhookEndpointUpdateRequest,
    WebhookSigningSecrets
} from '../types/settings-types';
import { BaseSettingsEndpoints } from './base';
import { ApiClientError } from './response/ApiClientError';
import { PaginatedResult } from './response/paginated_result';

const SETTINGS_BASE_PATH = '/v1/settings';
const ORGANIZATION_SETTINGS_PATH = `${SETTINGS_BASE_PATH}/organizations`;
const OFFICE_SETTINGS_PATH = `${SETTINGS_BASE_PATH}/offices`;
const ACCOUNT_SETTINGS_PATH = `${SETTINGS_BASE_PATH}/account`;
const WEBHOOK_SECRET_PATH = `${ORGANIZATION_SETTINGS_PATH}/webhook-secret`;
const WEBHOOKS_PATH = `${ORGANIZATION_SETTINGS_PATH}/webhooks`;

export class SettingsClient extends BaseSettingsEndpoints {
  async getOrganizationSettings(deviceTz?: string, forAccountSettings?: boolean) {
    const params = new URLSearchParams();
    if (deviceTz) params.set('user_tz', deviceTz);
    if (forAccountSettings) params.set('for_account_settings', 'true');
    const qs = params.toString();
    return this.get<OrganizationSettings>(`${ORGANIZATION_SETTINGS_PATH}${qs ? `?${qs}` : ''}`);
  }

  async getOfficeSettings(id: string) {
    return this.get<ReservationTimeSettingsResponse>(`${OFFICE_SETTINGS_PATH}/${id}`);
  }

  async updateOfficeSettings(id: string, settings: OfficeOrOrgSettings) {
    return this.update<OfficeOrOrgSettings, OfficeOrOrgSettings>(
      `${OFFICE_SETTINGS_PATH}/${id}`,
      settings
    );
  }

  async updateOrganizationSettings(settings: OrganizationSettings) {
    return this.update<OrganizationSettings, OrganizationSettings>(
      `${ORGANIZATION_SETTINGS_PATH}`,
      settings
    );
  }

  async updateOfficeDefaultReservationTime(id: string, settings: ReservationTimeSettings) {
    return this.updateOfficeSettings(id, {
      reservation_default_time: settings
    });
  }

  async updateOrganizationDefaultReservationTime(settings: ReservationTimeSettings) {
    return this.updateOrganizationSettings({
      reservation_default_time: settings
    });
  }

  async cancelCustomOfficeHours(id: string) {
    return this.delete(`${OFFICE_SETTINGS_PATH}/${id}`);
  }

  async cancelCustomOrganizationHours() {
    return this.delete(`${ORGANIZATION_SETTINGS_PATH}`);
  }

  async getAccountSettings() {
    return this.get<AccountSettings>(ACCOUNT_SETTINGS_PATH);
  }

  async updateAccountSettings(settings: Partial<AccountSettings>) {
    return this.update<Partial<AccountSettings>, AccountSettings>(
      ACCOUNT_SETTINGS_PATH,
      settings
    );
  }

  /**
   * Returns null when the organization has no signing secret yet: core answers
   * 404 for that case, which is a normal state rather than a failure.
   */
  async getWebhookSigningSecrets(): Promise<WebhookSigningSecrets | null> {
    try {
      const res = await this.get<WebhookSigningSecrets>(WEBHOOK_SECRET_PATH);
      return res.result;
    } catch (e) {
      if (e instanceof ApiClientError && e.response.status === 404) {
        return null;
      }
      throw e;
    }
  }

  /**
   * Schedules a new signing secret. The current secret keeps signing until
   * `activates_in_seconds` elapses, so consumers can adopt the new value first.
   */
  async rotateWebhookSigningSecret(
    activatesInSeconds: number
  ): Promise<WebhookSigningSecrets | null> {
    const res = await this.post<
      { activates_in_seconds: number },
      WebhookSigningSecrets
    >(`${WEBHOOK_SECRET_PATH}/rotate`, {
      activates_in_seconds: activatesInSeconds
    });
    return res.result;
  }

  async listWebhookEndpoints(): Promise<WebhookEndpoint[]> {
    const res = await this.get<WebhookEndpoint[]>(WEBHOOKS_PATH);
    return res.result ?? [];
  }

  async createWebhookEndpoint(
    body: WebhookEndpointCreateRequest
  ): Promise<WebhookEndpoint | null> {
    const res = await this.post<WebhookEndpointCreateRequest, WebhookEndpoint>(
      WEBHOOKS_PATH,
      body
    );
    return res.result;
  }

  async updateWebhookEndpoint(
    id: string,
    body: WebhookEndpointUpdateRequest
  ): Promise<WebhookEndpoint | null> {
    const res = await this.update<WebhookEndpointUpdateRequest, WebhookEndpoint>(
      `${WEBHOOKS_PATH}/${id}`,
      body
    );
    return res.result;
  }

  async deleteWebhookEndpoint(id: string) {
    return this.delete(`${WEBHOOKS_PATH}/${id}`);
  }

  /** Queues a `ping` delivery; works on disabled endpoints too. */
  async pingWebhookEndpoint(id: string): Promise<WebhookDelivery | null> {
    const res = await this.post<Record<string, never>, WebhookDelivery>(
      `${WEBHOOKS_PATH}/${id}/ping`,
      {}
    );
    return res.result;
  }

  /** Newest first. */
  async listWebhookEndpointDeliveries(
    id: string,
    params: ListWebhookDeliveriesParams = {}
  ): Promise<PaginatedResult<WebhookDeliverySummary> | null> {
    const queryParams = new URLSearchParams();
    if (params.cursor) queryParams.set('cursor', params.cursor);
    if (params.limit) queryParams.set('limit', String(params.limit));
    for (const status of params.status ?? []) {
      queryParams.append('status', status);
    }
    const qs = queryParams.toString();
    const res = await this.get<PaginatedResult<WebhookDeliverySummary>>(
      `${WEBHOOKS_PATH}/${id}/deliveries${qs ? `?${qs}` : ''}`
    );
    return res.result;
  }

  async getWebhookEndpointDelivery(
    id: string,
    deliveryId: string
  ): Promise<WebhookDelivery | null> {
    const res = await this.get<WebhookDelivery>(
      `${WEBHOOKS_PATH}/${id}/deliveries/${deliveryId}`
    );
    return res.result;
  }

  /**
   * Queues a new delivery of the same event. Core rejects it with
   * `delivery_in_progress` while the original is still being attempted.
   */
  async redeliverWebhookEndpointDelivery(
    id: string,
    deliveryId: string
  ): Promise<WebhookDelivery | null> {
    const res = await this.post<Record<string, never>, WebhookDelivery>(
      `${WEBHOOKS_PATH}/${id}/deliveries/${deliveryId}/redeliver`,
      {}
    );
    return res.result;
  }
}
